import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { TrackEvent, useTracking } from '../../common/tracking'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useExecuteAiAgentMutation } from '../../../../../store/ai/aiAgent'
import { agendaApi } from '../../../../../store/agenda/agenda'
import { useLazyGetAiAgentResultQuery } from '../../../../../store/ai/aiAgentResult'
import { selectSelectedMeetingTemplate, selectSessionMeetingTemplate } from '../../../../../store/meetingSummary/meetingTemplates'
import { useZocksAuth, useZocksQuota } from '../../../../../components/ScopedBlock/hooks'
import { AIAgentType } from '../../../../../models/AIAgent'
import { MeetingPrepExecutionResult } from '../../../../../models/MeetingPrep'
import { useMeetingPrep } from '../../../../../views/AIAgent/hooks/useMeetingPrep'
import { useExportToPDF } from '../../../../../components/PDFExportButton/hooks'
import { Col, Dropdown, OverlayTrigger, Row, Stack } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import BootstrapTooltip from 'react-bootstrap/Tooltip'
import { Prep } from '../Prep/Prep'
import { Chat } from '../AIAgentChat/Chat'
import { NotepadComponent } from '../Notepad/NotepadComponent'
import { RoomSession } from '../../../../../models/Room'
import { useMeetingUsers } from '../hooks/users'
import { formatDateHM } from '../../common/aifeatures/utils'
import { parseMetadata } from '../../../../../common/util'
import { PluginPromo, PromoType } from '../../../../../components/Promo/Plugin/PluginPromo'
import { useGetMeQuery } from '../../../../../store/user/user'
import { getIntegrationCategoryDisplayNameByServiceId, IntegrationServiceId } from '../../../../../common/integrations'
import { humanizeRemainingTime } from '../../../../../common/duration'
import { useSelectedMeetingTemplateHasAgendaEnabled } from '../Agenda/useAgendaFeature'
import { AddParticipantModal } from '../Participants/AddParticipantModal'
import { AutoMeetingPrepPromo } from '../AutoMeetingPrepPromo/AutoMeetingPrepPromo'
import { useAutoMeetingPrepMetadata } from '../../../../../views/Setup/Settings/AutoMeetingPrep/hooks'
import { hasTenMinutesPassed } from '../../../../../store/utils'
import './Interactions.scss'
import { Tasks } from '../Tasks/Tasks'
import { IntegrationIcon } from '../../../../../components/IntegrationIcon'
import { isLiveSession } from '../../../../../common/session.util'
import { useExportConfirmation } from '../../../../../components/ConfirmationDialog/useExportConfirmation'
import { MeetingPrepInsights } from '../MeetingPrepInsights/MeetingPrepInsights'

export const PREP_PDF_STYLES = `
      .prep-container {
        height: auto !important;
        max-width: 640px !important;
      }
      
      .border {
        border: none !important;
      }
    `

export interface InteractionsProps {
  roomSession: RoomSession
  onLoadingChange?: (loading: boolean) => void
  contactUpdatedServiceId?: string | null
  onDismissContactUpdate?: () => void
}

export const Interactions: React.FC<InteractionsProps> = ({ roomSession, onLoadingChange, contactUpdatedServiceId, onDismissContactUpdate }) => {
  const { tracked } = useTracking()
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const [triggerExecuteAiAgent, { isLoading: executeIsLoading }] = useExecuteAiAgentMutation()
  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()
  const exportPDFRef = useRef(null)
  const exportPDFRefWithoutAgenda = useRef(null)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const meetingTemplateId = selectedMeetingTemplate?.id
  const { hasScope } = useZocksAuth()
  const isAgendaEnabledForSelectedMeetingTemplate = useSelectedMeetingTemplateHasAgendaEnabled()
  const hasNoteReadScope = hasScope('**', 'meeting_note:read', ['meeting_note:admin'])
  const { roomId, id: sessionId } = roomSession
  const sessionMeetingTemplate = useAppSelector(selectSessionMeetingTemplate)
  const { usersByRoles, isFetching: contactsFetching } = useMeetingUsers(roomSession, ['external'])
  const contacts = usersByRoles['Clients'] ?? []
  const contactCount = contacts.length
  const clients = useMemo(() => (usersByRoles['Clients'] ?? []).map((contact) => contact.user), [usersByRoles])
  const [shouldAutoPrep, setShouldAutoPrep] = useState<boolean>(true)
  const [shouldPoll, setShouldPoll] = useState(false)
  const [showAddParticipantModal, setShowAddParticipantModal] = useState<boolean>(false)
  const { creator_service_id: creatorServiceId } = parseMetadata(roomSession.metadata)
  const { hasQuota } = useZocksQuota()
  const insightsAvailable = hasQuota('insights.enabled.value')

  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useExportConfirmation({ exportType: 'document' })

  const hasClients = contactCount > 0

  const _executeMeetingPrep = useCallback(
    async (selectedMeetingTemplateId: string) => {
      const prepPromise = triggerExecuteAiAgent({
        accountId,
        payload: {
          type: AIAgentType.MEETING_PREPARATION,
          context: {
            type: 'upcoming',
            roomId,
            sessionId,
            meetingTemplateId: selectedMeetingTemplateId,
          },
        },
      }).unwrap() as Promise<MeetingPrepExecutionResult>

      if (isAgendaEnabledForSelectedMeetingTemplate) {
        const agendaPromise = triggerExecuteAiAgent({
          accountId,
          payload: {
            type: AIAgentType.MEETING_AGENDA,
            context: {
              roomId,
              sessionId,
              meetingTemplateId: selectedMeetingTemplateId,
            },
          },
        }).unwrap()

        const [agendaResult, prepResult] = await Promise.allSettled([agendaPromise, prepPromise])

        if (agendaResult.status === 'rejected') {
          Sentry.captureException(agendaResult.reason)
          console.error('Failed to execute agenda:', agendaResult.reason)
        } else {
          dispatch(agendaApi.util.invalidateTags(['Agenda']))
        }

        if (prepResult.status === 'rejected') {
          throw prepResult.reason
        }

        return prepResult.value
      }

      return await prepPromise
    },
    [triggerExecuteAiAgent, accountId, roomId, sessionId, isAgendaEnabledForSelectedMeetingTemplate, dispatch],
  )

  const executeMeetingPrep = useMemo(
    () =>
      tracked(_executeMeetingPrep, {
        eventName: TrackEvent.MEETING_PREP_EXECUTED_FROM_UPCOMING,
        eventDetails: {
          roomId,
          sessionId,
          minutesBetweenMeetingAndPrep: Math.round((new Date(roomSession.startedAt).getTime() - new Date().getTime()) / 1000 / 60),
        },
      }),
    [_executeMeetingPrep, tracked, roomId, sessionId, roomSession.startedAt],
  )

  const getExistingMeetingPrep = useCallback(
    () =>
      triggerGetAiAgentResult(
        {
          accountId,
          type: AIAgentType.MEETING_PREPARATION,
          resultKey: `upcoming|${roomId}|${sessionId}`,
        },
        false,
      ).unwrap(),
    [triggerGetAiAgentResult, accountId, roomId, sessionId],
  )

  const { startedAt: autoMeetingPrepStartedAt, finishedAt: autoMeetingPrepFinishedAt } = useAutoMeetingPrepMetadata(roomSession)

  const {
    isLoading,
    interactionIsLoading,
    editIsLoading,
    existingMeetingPrep,
    meetingPrep,
    onMeetingPrepChatInteraction,
    handleResubmitInteraction,
    onMeetingPrepEditInteraction: _onMeetingPrepEditInteraction,
    onMeetingPrepExecution,
    aiAgentContext,
    citations,
    citationIsLoading,
  } = useMeetingPrep({ executeMeetingPrep, getExistingMeetingPrep, shouldPoll })

  const onMeetingPrepEditInteraction = useMemo(
    () =>
      tracked(_onMeetingPrepEditInteraction, {
        eventName: TrackEvent.MEETING_PREP_EDITED_MANUALLY,
        eventDetails: {
          roomId,
          sessionId,
        },
      }),
    [_onMeetingPrepEditInteraction, tracked, roomId, sessionId],
  )

  useEffect(() => {
    setShouldPoll(!meetingPrep && !!autoMeetingPrepStartedAt && !autoMeetingPrepFinishedAt && !hasTenMinutesPassed(autoMeetingPrepStartedAt))
  }, [meetingPrep, autoMeetingPrepStartedAt, autoMeetingPrepFinishedAt])

  const _handleRerun = async () => {
    if (meetingTemplateId) {
      await onMeetingPrepExecution(meetingTemplateId).catch(console.error)
    }
  }

  const handleRerun = tracked(
    () => {
      _handleRerun()
      onDismissContactUpdate?.()
    },
    {
      eventName: TrackEvent.MEETING_PREP_RERUN,
      eventDetails: {
        roomId,
        sessionId,
        meetingTemplateId,
      },
    },
  )

  const handleGenerateMeetingPrep = tracked(_handleRerun, {
    eventName: TrackEvent.MEETING_PREP_OPENED,
    eventDetails: {
      roomId,
      sessionId,
      meetingTemplateId,
      source: isLiveSession(roomSession) ? 'live' : 'upcoming',
    },
  })

  useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  useEffect(() => {
    if (!shouldAutoPrep) {
      return
    }
    if (isLoading || !meetingTemplateId || contactsFetching) {
      return
    }
    if (shouldPoll || existingMeetingPrep || meetingPrep || !contactCount) {
      setShouldAutoPrep(false)
      return
    }
    setShouldAutoPrep(false)
    handleGenerateMeetingPrep()
  }, [
    contactCount,
    contactsFetching,
    executeIsLoading,
    existingMeetingPrep,
    handleGenerateMeetingPrep,
    isLoading,
    meetingPrep,
    meetingTemplateId,
    shouldAutoPrep,
    shouldPoll,
  ])

  const { exportToPDF } = useExportToPDF(exportPDFRef, PREP_PDF_STYLES)

  const { exportToPDF: exportPdfWithoutAgenda } = useExportToPDF(exportPDFRefWithoutAgenda, PREP_PDF_STYLES)

  const handleExportToPDF = async () =>
    await runMutationWithConfirmation(
      tracked(exportToPDF, {
        eventName: TrackEvent.EXPORT_TO_PDF,
        eventDetails: {
          roomId,
          sessionId,
          meetingTemplateId,
          type: 'meetingPrep',
        },
      }),
    )

  const handleExportToPDFWithoutAgenda = async () =>
    await runMutationWithConfirmation(
      tracked(exportPdfWithoutAgenda, {
        eventName: TrackEvent.EXPORT_TO_PDF,
        eventDetails: {
          roomId,
          sessionId,
          meetingTemplateId,
          type: 'meetingPrep_without_agenda',
        },
      }),
    )

  const PrepInfo = () => {
    const [now, setNow] = useState(new Date())

    const { startedAt, finishedAt } = roomSession

    const startBeforeSuffix =
      !creatorServiceId && roomSession.finishedAt && roomSession.finishedAt > new Date().toISOString()
        ? `, or start anytime before ${formatDateHM(roomSession.finishedAt)}`
        : ''

    const scheduledForPrefix = useMemo(() => {
      const startedAtDate = new Date(roomSession.startedAt)
      const isPast = startedAtDate.getTime() - now.getTime() < 0
      return !isPast ? `Meeting scheduled for ${humanizeRemainingTime(new Date(roomSession.startedAt), now)} from now. ` : ''
    }, [now])

    useEffect(() => {
      if (!roomSession.finishedAt || creatorServiceId || roomSession.startedAt <= new Date().toISOString()) {
        return
      }
      const scheduledForTimeout = setTimeout(() => setNow(new Date()), 60000)
      return () => clearTimeout(scheduledForTimeout)
    }, [now, finishedAt, startedAt])

    return (
      <div className='d-flex flex-column gap-4 pt-4 border border-1 zocks-border-color-gray-300 zocks-border-0.5 zocks-bg-color-gray-100'>
        <div className='d-flex gap-3 px-3'>
          <i className='bi bi-text-left fs-5 pt-1' />
          <div className='d-flex flex-column gap-2'>
            <h5>Get prepared</h5>
            <div className='paragraph-medium'>{`${scheduledForPrefix}Add client details and select the right Meeting Type to generate Meeting Prep${startBeforeSuffix}.`}</div>
          </div>
        </div>
        <div className='ps-5'>
          <Button
            variant='primary'
            className='button-small text-nowrap'
            loading={isLoading}
            disabled={!contactCount || !meetingTemplateId}
            onClick={handleGenerateMeetingPrep}
          >
            Generate Meeting Prep
          </Button>
        </div>
        <AutoMeetingPrepPromo />
      </div>
    )
  }

  const integrationDisplayName = contactUpdatedServiceId ? getIntegrationCategoryDisplayNameByServiceId(contactUpdatedServiceId) : ''

  const [activeTab, setActiveTab] = useState<'meetingPrep' | 'tasks'>('meetingPrep')

  const _changeTab = (key: 'meetingPrep' | 'tasks') => {
    setActiveTab(key)
  }

  const changeTab = tracked(
    _changeTab,
    { eventName: TrackEvent.MEETING_PREP_TAB_CHANGED },
    {
      eventDetailsExtractor: (key) => (key ? { tab: key } : {}),
    },
  )

  const tabConfig = [
    { key: 'meetingPrep', label: 'Meeting Prep' },
    { key: 'tasks', label: 'Tasks' },
  ] as const

  const TabHeader = () => (
    <div className='border-bottom d-flex mb-4 gap-4'>
      {tabConfig.map((tab) => (
        <button
          key={tab.key}
          className={`btn btn-link text-decoration-none text-uppercase paragraph-medium bold zocks-text-gray-900 pb-3 px-0 border-0 rounded-0 border-bottom border-2 ${
            activeTab === tab.key ? 'zocks-border-color-primary' : 'border-white'
          }`}
          onClick={() => changeTab(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )

  return (
    <>
      <ConfirmationModal />
      <div className='d-flex flex-column gap-4'>
        {showAddParticipantModal ? (
          <AddParticipantModal
            show={showAddParticipantModal}
            userAllocationContextUsers={Object.values(usersByRoles).flat()}
            onCancel={() => setShowAddParticipantModal(false)}
            onSuccess={() => setShowAddParticipantModal(false)}
          />
        ) : null}
        {hasClients ? <TabHeader /> : null}
        <Row className='d-flex mx-0 flex-row gap-4'>
          <Col className='d-flex flex-column px-0 gap-4'>
            {activeTab === 'meetingPrep' ? (
              <>
                {meetingPrep ? (
                  <Row className='mx-0 d-flex flex-row gap-3'>
                    <Col xs='auto' className='px-0'>
                      <Dropdown>
                        <Dropdown.Toggle disabled={isLoading} as={Button} variant='header' className='d-flex align-items-center' id='export-pdf-dropdown'>
                          <Stack direction='horizontal' gap={2}>
                            <i className='bi bi-file-pdf' />
                            <span>Export to PDF</span>
                          </Stack>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={handleExportToPDFWithoutAgenda} disabled={accountIsLoading}>
                            Summary
                          </Dropdown.Item>
                          <Dropdown.Item onClick={handleExportToPDF} disabled={accountIsLoading}>
                            Agenda and Summary
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </Col>
                    <Col xs='auto' className='px-0'>
                      <OverlayTrigger
                        trigger={citationIsLoading ? ['hover', 'focus'] : []}
                        placement='top'
                        overlay={
                          <BootstrapTooltip className='zocks-text-gray-500'>
                            Rerun is disabled while references are being generated. Please hold on.
                          </BootstrapTooltip>
                        }
                      >
                        <Button variant='header' onClick={handleRerun} disabled={isLoading || citationIsLoading}>
                          <Stack direction='horizontal' gap={2}>
                            <i className='bi bi-arrow-clockwise' />
                            <span>Rerun</span>
                          </Stack>
                        </Button>
                      </OverlayTrigger>
                    </Col>
                  </Row>
                ) : null}
                {contactUpdatedServiceId ? (
                  <Row className='zocks-mb-1 d-flex p-3 m-0 zocks-bg-color-primary-light zocks-border-0.5 gap-3 justify-content-center rounded'>
                    <div className=' d-flex align-items-center justify-content-between'>
                      <div className='d-flex gap-3 align-items-center'>
                        <div className='d-flex gap-3 align-items-center'>
                          <i className='bi bi-exclamation-circle' />
                          <div className='paragraph-medium'>
                            Contact linked to the {integrationDisplayName}. Rerun Meeting Prep to see updated materials with the latest client data.
                          </div>
                        </div>
                        <Button variant='primary' className='button-large' onClick={handleRerun}>
                          Rerun
                        </Button>
                      </div>
                      <div>
                        <Button variant='borderless' className='zocks-bg-color-primary-light' onClick={onDismissContactUpdate}>
                          <i className='bi bi-x' />
                        </Button>
                      </div>
                    </div>
                  </Row>
                ) : null}
                {!contactCount ? (
                  <div className='d-flex flex-column gap-4 py-4 px-3 border border-1 zocks-border-color-gray-300 zocks-border-0.5 zocks-bg-color-gray-100'>
                    <div className='d-flex gap-3'>
                      <i className='bi bi-people fs-5 pt-1' />
                      <div className='d-flex flex-column gap-2'>
                        <h5>Add Clients to generate Meeting Prep</h5>
                        <div className='paragraph-medium'>Your AI Assistant will create personalized prep materials based on client profiles and history.</div>
                      </div>
                    </div>
                    <div className='zocks-pl-2.0'>
                      <Button variant='primary' className='button-small' onClick={() => setShowAddParticipantModal(true)}>
                        <i className='bi bi-plus-circle' />
                        Add Client
                      </Button>
                    </div>
                  </div>
                ) : meetingPrep || isLoading ? (
                  <Prep
                    isLoading={isLoading}
                    interactionIsLoading={interactionIsLoading}
                    editIsLoading={editIsLoading}
                    existingMeetingPrep={existingMeetingPrep}
                    meetingPrep={meetingPrep}
                    onMeetingPrepEditInteraction={onMeetingPrepEditInteraction}
                    onMeetingPrepExecution={onMeetingPrepExecution}
                    citations={citations}
                    citationIsLoading={citationIsLoading}
                    agentMeetingTemplateId={aiAgentContext?.meetingTemplateId}
                    exportPDFRef={exportPDFRef}
                    exportPDFRefWithoutAgenda={exportPDFRefWithoutAgenda}
                  />
                ) : (
                  <PrepInfo />
                )}
              </>
            ) : (
              <Tasks accountId={accountId} clients={clients} />
            )}
          </Col>
          <Col className='d-flex flex-column min-vh-100 px-0'>
            <Stack gap={4}>
              <MeetingPrepPluginPromo roomSession={roomSession} />
              {insightsAvailable && sessionMeetingTemplate && <MeetingPrepInsights meetingTemplateId={sessionMeetingTemplate.id} />}
              <Chat
                isLoading={isLoading}
                handleResubmitInteraction={handleResubmitInteraction}
                interactionIsLoading={interactionIsLoading}
                disabled={!meetingPrep || interactionIsLoading || editIsLoading || citationIsLoading}
                onMeetingPrepChatInteraction={onMeetingPrepChatInteraction}
              />
              {hasNoteReadScope ? <NotepadComponent /> : null}
            </Stack>
          </Col>
        </Row>
      </div>
    </>
  )
}

export interface MeetingPrepPluginPromoProps {
  roomSession: RoomSession
}

const MeetingPrepPluginPromo: React.FC<MeetingPrepPluginPromoProps> = ({ roomSession }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: currentUser } = useGetMeQuery({ accountId })

  const { source } = useMemo(() => (roomSession ? parseMetadata(roomSession.metadata) : {}), [roomSession])

  const hasZoomIntegration = useMemo(() => {
    return !!currentUser?.integrations?.some((i) => i.serviceId === IntegrationServiceId.ZOOM)
  }, [currentUser])

  if (source !== IntegrationServiceId.ZOOM || !hasZoomIntegration) {
    return null
  }

  return (
    <PluginPromo
      Icon={<IntegrationIcon serviceId={IntegrationServiceId.ZOOM} width={32} height={32} />}
      InstallButton={
        <a
          className='button-link-medium zoom-promo-button text-decoration-none p-2 zocks-border-0.25'
          href='https://marketplace.zoom.us/apps/an_KQeraSPyMEKowV9KDvQ'
          target='_blank'
          rel='noreferrer noopeener'
        >
          Install Zoom App
        </a>
      }
      header='Bring Zocks to live Zoom meetings'
      body='Get meeting prep materials, ask AI Assistant and make notes directly in Zoom. No switching between apps needed.'
      containerClassname='zoom-promo-container'
      promoType={PromoType.ZOOM}
    />
  )
}
